import { useState, useRef, useEffect } from 'react';
import { Mic, MicOff, Square, X, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/axios';
import { getErrorMessage } from '@/lib/errors';
import { useIkalStore } from '@/store/ikal.store';
import { IkalOrb } from '@/components/ikal/IkalOrb';

interface Props {
  citaId: string;
  pacienteId: string;
  onClose: () => void;
}

const hasSpeechRecognition =
  typeof window !== 'undefined' &&
  ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);

export function TranscripcionConsulta({ citaId, pacienteId, onClose }: Props) {
  const { openIkal, queueMessage } = useIkalStore();
  const [transcript, setTranscript] = useState('');
  const [recording, setRecording]   = useState(false);
  const [secs, setSecs]             = useState(0);
  const [audioBlob, setAudioBlob]   = useState<Blob | null>(null);
  const [isSaving, setIsSaving]     = useState(false);

  const recognitionRef = useRef<any>(null);
  const mediaRecRef    = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const finalRef       = useRef('');
  const recordingRef   = useRef(false);
  const intervalRef    = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => () => {
    recordingRef.current = false;
    recognitionRef.current?.stop();
    mediaRecRef.current?.stop();
    if (intervalRef.current) clearInterval(intervalRef.current);
  }, []);

  async function startRecording() {
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      toast.error(getErrorMessage(err, 'No se pudo acceder al micrófono'));
      return;
    }

    audioChunksRef.current = [];
    const mr = new MediaRecorder(stream, { mimeType: 'audio/webm' });
    mr.ondataavailable = e => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
    mr.onstop = () => {
      stream.getTracks().forEach(t => t.stop());
      setAudioBlob(new Blob(audioChunksRef.current, { type: 'audio/webm' }));
    };
    mr.start(200);
    mediaRecRef.current = mr;

    if (hasSpeechRecognition) {
      const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      finalRef.current = transcript ? transcript.trimEnd() + ' ' : '';
      const recognition = new SR();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'es-MX';
      recognition.onresult = (event: any) => {
        let interim = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          if (event.results[i].isFinal) {
            finalRef.current += event.results[i][0].transcript + ' ';
          } else {
            interim += event.results[i][0].transcript;
          }
        }
        setTranscript(finalRef.current + interim);
      };
      recognition.onerror = (event: any) => {
        if (event.error !== 'no-speech') toast.error(`Error en el reconocimiento de voz (${event.error})`);
      };
      recognition.onend = () => {
        if (recordingRef.current) try { recognition.start(); } catch {}
      };
      recognition.start();
      recognitionRef.current = recognition;
    }

    recordingRef.current = true;
    setRecording(true);
    setSecs(0);
    intervalRef.current = setInterval(() => setSecs(s => s + 1), 1000);
  }

  function stopRecording() {
    recordingRef.current = false;
    setRecording(false);
    if (hasSpeechRecognition) {
      recognitionRef.current?.stop();
      recognitionRef.current = null;
      setTranscript(finalRef.current.trimEnd());
    }
    mediaRecRef.current?.stop();
    mediaRecRef.current = null;
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }

  async function handleGuardar() {
    if (!audioBlob && !transcript.trim()) return;
    setIsSaving(true);
    try {
      const fd = new FormData();
      fd.append('tipo', 'grabacion_consulta');
      if (transcript.trim()) fd.append('contenido', transcript.trim());
      if (audioBlob) fd.append('audio', audioBlob, 'grabacion-consulta.webm');
      await api.post(`/api/pacientes/${pacienteId}/notas`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Grabación guardada en el expediente');
      setAudioBlob(null);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Error al guardar la grabación'));
    } finally {
      setIsSaving(false);
    }
  }

  function handleAnalizar() {
    const text = transcript.trim();
    if (text.length < 10) return toast.error('La transcripción es muy corta');
    if (recording) stopRecording();
    openIkal({ citaId, pacienteId });
    queueMessage(
      `Analiza la siguiente transcripción de consulta médica y proporciona:\n` +
      `1. Resumen de la consulta\n` +
      `2. Notas clínicas sugeridas\n` +
      `3. Diagnósticos CIE-11 probables (código + nombre)\n` +
      `4. Medicación sugerida (nombre, dosis, frecuencia, duración)\n` +
      `5. Estudios o laboratorios recomendados\n\n` +
      `Transcripción:\n${text}`,
    );
    onClose();
  }

  const mmss = `${String(Math.floor(secs / 60)).padStart(2, '0')}:${String(secs % 60).padStart(2, '0')}`;
  const hasRecording = !!audioBlob;
  const hasTranscript = transcript.trim().length >= 10;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-2xl max-h-[92vh] flex flex-col shadow-2xl">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <div>
            <h3 className="font-semibold text-text-primary">Escuchar consulta</h3>
            <p className="text-[11px] text-text-secondary mt-0.5">
              Graba la consulta · se guarda audio y transcripción en el expediente
            </p>
          </div>
          <button type="button" onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-text-secondary transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          <div className="space-y-4">

            {/* Barra de grabación */}
            {recording ? (
              <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse flex-shrink-0" />
                <span className="text-sm font-medium text-red-700 tabular-nums">{mmss}</span>
                <span className="text-sm text-red-600 flex-1">Grabando…</span>
                <button type="button" onClick={stopRecording}
                  className="flex items-center gap-1.5 bg-white border border-red-200 text-red-700 px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-red-50 transition-colors">
                  <Square size={11} /> Detener
                </button>
              </div>
            ) : hasRecording ? (
              <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl px-4 py-3">
                <span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
                <span className="text-sm text-green-700 flex-1">
                  Grabación lista · {mmss} grabados
                </span>
                <button type="button" onClick={startRecording}
                  className="flex items-center gap-1.5 bg-white border border-green-200 text-green-700 px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-green-50 transition-colors">
                  <Mic size={11} /> Nueva
                </button>
              </div>
            ) : (
              <button type="button" onClick={startRecording}
                className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-4 py-2.5 rounded-xl text-sm font-medium transition-colors">
                <Mic size={14} /> Iniciar grabación
              </button>
            )}

            {!hasSpeechRecognition && (
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                <MicOff size={14} className="text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-amber-700">
                  El audio se graba normalmente. La transcripción en vivo no está disponible en este navegador — escríbela o pégala manualmente.
                </p>
              </div>
            )}

            {/* Transcripción */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-text-secondary">
                  Transcripción
                </label>
                {transcript && (
                  <button type="button" onClick={() => setTranscript('')}
                    className="text-xs text-text-secondary hover:text-red-500 transition-colors">
                    Limpiar
                  </button>
                )}
              </div>
              <textarea
                value={transcript}
                onChange={e => setTranscript(e.target.value)}
                rows={8}
                placeholder={recording ? 'Habla… el texto aparecerá aquí.' : 'Escribe o pega la transcripción de la consulta…'}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 resize-none"
              />
              <p className="text-[11px] text-text-secondary mt-1">{transcript.length} caracteres</p>
            </div>

            {/* Acciones */}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleGuardar}
                disabled={isSaving || (!hasRecording && !hasTranscript) || recording}
                className="flex items-center gap-1.5 border border-gray-200 text-text-secondary hover:bg-gray-50 hover:border-gray-300 px-4 py-2.5 rounded-xl text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                {isSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                {isSaving ? 'Guardando…' : 'Guardar en expediente'}
              </button>

              <button
                type="button"
                onClick={handleAnalizar}
                disabled={!hasTranscript || recording}
                className="flex-1 flex items-center justify-center gap-2 text-white px-4 py-2.5 rounded-xl text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                style={{ backgroundColor: '#3d5e5a' }}
                onMouseEnter={e => { if (hasTranscript && !recording) e.currentTarget.style.backgroundColor = '#2e4a47'; }}
                onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#3d5e5a'; }}
              >
                <IkalOrb size={16} />
                Analizar con Ikal
              </button>
            </div>

            {hasRecording && (
              <p className="text-[11px] text-text-secondary text-center -mt-1">
                Guarda el audio y la transcripción en el expediente · solo lectura una vez guardado
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
